<template>
  <div class="bag-photo-upload">
    <div class="bag-photo-upload__header">
      <h3>Add luggage photos</h3>
      <p>{{ helperText }}</p>
      <strong class="bag-photo-upload__counter">{{ uploadCounterText }}</strong>
    </div>

    <div class="bag-photo-upload__grid">
      <button
        v-for="(container, index) in countContainers"
        :key="container"
        type="button"
        class="bag-photo-upload__slot"
        :class="{
          'bag-photo-upload__slot--filled': images[index],
          'bag-photo-upload__slot--loading': load && uploadingIndex === index,
          'bag-photo-upload__slot--uploaded': slotStates[index] === 'uploaded'
        }"
        :disabled="load"
        @click="openExplorer(index)"
      >
        <input
          type="file"
          ref="file"
          accept="image/*,.heic,.heif"
          @change="handleFileUpload(index)"
        >

        <span v-if="load && uploadingIndex === index" class="bag-photo-upload__slot-overlay">
          <span class="bag-photo-upload__spinner"></span>
          <span>{{ progressPercent }}%</span>
          <span>{{ progressLabel }}</span>
          <span class="bag-photo-upload__progress">
            <span :style="{ width: `${progressPercent}%` }"></span>
          </span>
        </span>

        <img
          v-if="urlsImages[index] && previewableImages[index]"
          class="bag-photo-upload__preview"
          :src="urlsImages[index]"
          :alt="`Bag ${index + 1} preview`"
          @error="previewableImages.splice(index, 1, false)"
        >

        <span v-else-if="images[index]" class="bag-photo-upload__file">
          <span class="bag-photo-upload__file-type">{{ fileTypeLabel(index) }}</span>
          <span>{{ fileNames[index] || `Bag ${index + 1} photo` }}</span>
          <small>{{ slotStates[index] === 'uploaded' ? 'Photo saved. Preview is not available.' : 'Preview is not available.' }}</small>
        </span>

        <span v-else class="bag-photo-upload__placeholder">
          <img src="@/assets/images/customer_order/camera-icon.svg" alt="">
          <span>Bag {{ index + 1 }}</span>
          <small>Tap to add photo</small>
        </span>

        <span v-if="slotStates[index] === 'uploaded' && !(load && uploadingIndex === index)" class="bag-photo-upload__badge">
          Uploaded
        </span>

        <span v-if="slotStates[index] === 'failed'" class="bag-photo-upload__badge bag-photo-upload__badge--failed">
          Not uploaded
        </span>

        <img
          v-if="images[index] && !(load && uploadingIndex === index)"
          class="bag-photo-upload__delete"
          src="@/assets/images/customer_order/trash-can.png"
          alt="Delete photo"
          title="Delete photo"
          @click.stop="deletePhoto(index)"
        >
      </button>
    </div>

    <div class="bag-photo-upload__status" :class="{ 'bag-photo-upload__status--error': uploadError }">
      <span v-if="load" class="bag-photo-upload__spinner bag-photo-upload__spinner--small"></span>
      <span class="bag-photo-upload__status-text">{{ statusText }}</span>
      <span v-if="load" class="bag-photo-upload__progress bag-photo-upload__progress--status">
        <span :style="{ width: `${progressPercent}%` }"></span>
      </span>
    </div>

    <button
      v-if="uploadedOnce && !load"
      type="button"
      class="customer-order-modal--upload-button bag-photo-upload__done"
      @click="finishUpload"
    >
      DONE
    </button>
  </div>
</template>

<script>
import { prepareLuggagePhoto } from '@/utils/prepareLuggagePhoto'

export default {
  data () {
    return {
      file: '',
      urlsImages: [],
      images: [],
      fileNames: [],
      previewableImages: [],
      slotStates: [],
      load: false,
      uploadedOnce: false,
      uploadError: '',
      uploadingIndex: null,
      progressPercent: 0,
      uploadPhase: ''
    }
  },
  props: ['countContainers', 'customerInfo', 'orderInfo'],
  mounted () {
    this.hydrateUploadedImages()
  },
  computed: {
    selectedCount () {
      return this.images.filter(Boolean).length
    },
    helperText () {
      if (this.load) {
        return 'Please wait while the selected photo is uploading.'
      }
      if (this.selectedCount) {
        return 'Photos upload automatically after selection. Add another bag photo or tap Done.'
      }
      return 'Select one photo per bag. Upload starts automatically.'
    },
    statusText () {
      if (this.uploadError) {
        return this.uploadError
      }
      if (this.load) {
        return this.progressLabel
      }
      if (this.slotStates.includes('failed')) {
        return 'Some photos were not uploaded. Tap their slots to select a photo again.'
      }
      if (this.uploadedOnce) {
        return `Photos uploaded successfully. ${this.uploadCounterText}.`
      }
      return 'No photos selected yet.'
    },
    progressLabel () {
      if (this.uploadPhase === 'optimizing') {
        return 'Preparing photo...'
      }
      if (this.uploadPhase === 'processing') {
        return 'Processing photo...'
      }
      if (this.uploadPhase === 'starting') {
        return 'Preparing upload...'
      }
      return 'Uploading photo...'
    },
    uploadCounterText () {
      return `${this.slotStates.filter(state => state === 'uploaded').length} of ${this.countContainers} bag photos uploaded`
    }
  },
  methods: {
    hydrateUploadedImages () {
      const uploadedImages = Array.isArray(this.orderInfo.imgUrl) ? this.orderInfo.imgUrl : []

      if (!uploadedImages.length) {
        return
      }

      this.images = uploadedImages.slice(0, this.countContainers)
      this.urlsImages = uploadedImages.slice(0, this.countContainers)
      this.fileNames = uploadedImages.map((image, index) => `Bag ${index + 1} photo`)
      this.previewableImages = uploadedImages.map(() => true)
      this.slotStates = uploadedImages.map(() => 'uploaded')
      this.uploadedOnce = true
    },
    async handleFileUpload (index) {
      const file = this.$refs.file[index].files[0]

      if (!file) {
        return
      }

      if (this.load || this.slotStates[index] === 'uploaded') return
      this.load = true
      this.uploadingIndex = index
      this.uploadPhase = 'optimizing'
      this.progressPercent = 0
      this.uploadError = ''
      this.fileNames[index] = file.name
      this.fileNames = [...this.fileNames]
      this.$set(this.slotStates, index, 'loading')
      try {
        const { file: uploadFile, canPreview } = await prepareLuggagePhoto(file)
        this.previewableImages[index] = canPreview
        this.previewableImages = [...this.previewableImages]
        this.images[index] = await this.fileToBase64(uploadFile)
        this.images = [...this.images]
        this.urlsImages[index] = this.previewableImages[index] ? this.images[index] : ''
        this.urlsImages = [...this.urlsImages]
        await this.uploadSelectedImages(index)
      } catch (error) {
        this.markUploadError(error.message)
      } finally {
        if (this.$refs.file?.[index]) this.$refs.file[index].value = ''
        this.load = false
      }
    },
    fileToBase64 (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    openExplorer (index) {
      if (this.load || this.slotStates[index] === 'uploaded') {
        return
      }
      this.$refs.file[index].click()
    },
    async uploadSelectedImages (index) {
      if (!this.selectedCount) {
        return
      }

      this.load = true
      this.uploadingIndex = index
      this.progressPercent = 0
      this.uploadPhase = 'starting'
      this.slotStates[index] = 'loading'
      this.slotStates = [...this.slotStates]
      await this.uploadImg(index)
    },
    async uploadImg (index) {
      if (this.customerInfo.mockOnly || this.orderInfo.mockOnly) {
        await this.runMockUpload()
        this.markUploadSuccess({ uploadedUrls: [this.images[index]], imgUrl: [this.images[index]] })
        return
      }

      const today = new Date()
      const totalObj = {
        customerId: this.customerInfo._id,
        orderNumber: this.orderInfo.orderNumber,
        orderInfo: {
          orderChangedDate: `${Date.parse(today)}`
        },
        images: [this.images[index]],
        addImg: this.uploadedOnce || Boolean(this.orderInfo.imgUrl && this.orderInfo.imgUrl.length)
      }

      try {
        const response = await this.uploadWithProgress(totalObj)
        if (response.status === 200) {
          this.markUploadSuccess(JSON.parse(response.responseText))
        } else {
          this.markUploadError(response.responseText)
        }
      } catch (error) {
        this.markUploadError(error && error.message)
      }
    },
    runMockUpload () {
      return new Promise(resolve => {
        let progress = 0
        const timer = setInterval(() => {
          progress += 12
          this.progressPercent = Math.min(progress, 100)
          this.uploadPhase = this.progressPercent >= 100 ? 'processing' : 'uploading'
          if (this.progressPercent >= 100) {
            clearInterval(timer)
            setTimeout(resolve, 350)
          }
        }, 90)
      })
    },
    uploadWithProgress (payload) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        const apiUrl = `${process.env.VUE_APP_PROD_MODE === 'true'
          ? process.env.VUE_APP_API_SERVER
          : 'http://localhost:3100'}/up-img`

        xhr.open('POST', apiUrl)
        xhr.timeout = 120000
        xhr.setRequestHeader('Content-Type', 'application/json')

        xhr.upload.onloadstart = () => {
          this.uploadPhase = 'uploading'
          this.progressPercent = 1
        }

        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) {
            return
          }
          this.progressPercent = Math.min(100, Math.round((event.loaded / event.total) * 100))
          this.uploadPhase = this.progressPercent >= 100 ? 'processing' : 'uploading'
        }

        xhr.onload = () => {
          this.progressPercent = 100
          this.uploadPhase = ''
          resolve({
            status: xhr.status,
            responseText: xhr.responseText
          })
        }

        xhr.onerror = reject
        xhr.ontimeout = reject
        xhr.send(JSON.stringify(payload))
      })
    },
    isHeicFile (file) {
      return /image\/hei(c|f)/i.test(file.type) || /\.(heic|heif)$/i.test(file.name)
    },
    isPreviewableFile (file) {
      return /^image\//i.test(file.type) && !this.isHeicFile(file)
    },
    fileTypeLabel (index) {
      const fileName = this.fileNames[index] || ''
      const extension = fileName.split('.').pop()

      if (!extension || extension === fileName) {
        return 'IMAGE'
      }

      return extension.toUpperCase()
    },
    normalizeUploadError (errorText = '') {
      try {
        const error = JSON.parse(errorText)
        errorText = error.message || error.error?.message || errorText
      } catch (_) { /* Plain text errors are also supported. */ }
      if (/too large|maximum.*10|smaller.*10/i.test(errorText)) {
        return 'Photo is too large. Please select a smaller image (maximum 10 MB).'
      }
      if (/could not|cannot|25 MB|not confirmed|already uploaded/i.test(errorText)) return errorText
      return 'Upload failed. Please try again.'
    },
    markUploadSuccess (result) {
      const index = this.uploadingIndex
      const url = result?.uploadedUrls?.[0]
      if (!url || !Array.isArray(result.imgUrl) || !result.imgUrl.includes(url)) {
        throw new Error('Photo saving was not confirmed. Please reopen the order before retrying.')
      }
      if (this.images.some((photo, slot) => slot !== index && photo === url && this.slotStates[slot] === 'uploaded')) {
        throw new Error('This photo is already uploaded. Please select a different photo.')
      }
      this.$set(this.images, index, url)
      this.$set(this.urlsImages, index, url)
      this.$set(this.previewableImages, index, !/\.hei[cf](?:$|\?)/i.test(url))
      this.$set(this.slotStates, index, 'uploaded')
      this.load = false
      this.uploadedOnce = true
      this.uploadError = ''
      this.uploadingIndex = null
      this.progressPercent = 100
      this.uploadPhase = ''
      this.$emit('photosUploaded')
    },
    markUploadError (errorText) {
      if (this.uploadingIndex !== null) this.$set(this.slotStates, this.uploadingIndex, 'failed')
      this.load = false
      this.uploadingIndex = null
      this.uploadPhase = ''
      this.uploadError = this.normalizeUploadError(errorText)
    },
    finishUpload () {
      this.$emit('closeUploadModal')
    },
    confirmPhotoDeletion () {
      return window.confirm('Delete this photo? This action cannot be undone.')
    },
    getCloudinaryPublicId (imageUrl) {
      const fileNameStart = imageUrl.lastIndexOf('/') + 1
      const extensionStart = imageUrl.lastIndexOf('.')

      if (fileNameStart <= 0 || extensionStart <= fileNameStart) {
        return ''
      }

      return imageUrl.slice(fileNameStart, extensionStart)
    },
    async deletePhoto (index) {
      const imageUrl = this.urlsImages[index]

      if (!this.confirmPhotoDeletion()) {
        return
      }

      if (!/^https?:\/\//i.test(imageUrl)) {
        this.$set(this.images, index, '')
        this.$set(this.urlsImages, index, '')
        this.$set(this.slotStates, index, '')
        this.uploadError = ''
        return
      }

      const today = new Date()
      const totalObj = {
        customerId: this.customerInfo._id,
        orderNumber: this.orderInfo.orderNumber,
        link: imageUrl,
        orderInfo: {
          orderChangedDate: `${Date.parse(today)}`
        },
        id: this.getCloudinaryPublicId(imageUrl)
      }

      try {
        const response = await fetch(
          `${process.env.VUE_APP_PROD_MODE === 'true'
            ? process.env.VUE_APP_API_SERVER
            : 'http://localhost:3100'}/rem-img`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(totalObj)
          })

        if (response.status !== 200) {
          alert('Something went wrong!')
          return
        }

        this.images.splice(index, 1)
        this.urlsImages.splice(index, 1)
        this.fileNames.splice(index, 1)
        this.previewableImages.splice(index, 1)
        this.slotStates.splice(index, 1)
        this.images = [...this.images]
        this.urlsImages = [...this.urlsImages]
        this.fileNames = [...this.fileNames]
        this.previewableImages = [...this.previewableImages]
        this.slotStates = [...this.slotStates]
        this.uploadedOnce = Boolean(this.images.length)
        this.$emit('arrayUrlPhotosGet')
      } catch {
        alert('Something went wrong!')
      }
    }
  }
}
</script>

<style lang="scss">
.bag-photo-upload {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  color: #3e3e3e;

  &__header {
    h3 {
      margin: 0 0 4px;
      font-size: 20px;
      letter-spacing: 0;
      text-align: center;
    }

    p {
      margin: 0;
      font-size: 15px;
      line-height: 1.35;
      text-align: center;
    }
  }

  &__counter {
    display: block;
    margin-top: 10px;
    padding: 8px 10px;
    border: 1px solid #2b9f5b;
    border-radius: 4px;
    background: #eef8f2;
    color: #247f4a;
    font-size: 15px;
    text-align: center;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
    width: 100%;
  }

  &__slot {
    position: relative;
    min-height: 150px;
    overflow: hidden;
    padding: 0;
    border: 2px dashed #969696;
    border-radius: 6px;
    background: #ffffff;
    color: #3e3e3e;
    cursor: pointer;

    &:disabled {
      cursor: wait;
      opacity: 0.85;
    }

    input {
      display: none;
    }

    &--filled {
      border-style: solid;
      border-color: #00a9e0;
    }

    &--uploaded {
      border-color: #2b9f5b;
    }
  }

  &__placeholder {
    display: flex;
    min-height: 150px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 16px;
    text-align: center;

    img {
      width: 36px;
      height: 36px;
      object-fit: contain;
    }

    span {
      font-size: 17px;
      font-weight: 600;
    }

    small {
      font-size: 13px;
      color: #707070;
    }
  }

  &__preview {
    display: block;
    width: 100%;
    height: 150px;
    object-fit: cover;
  }

  &__file {
    display: flex;
    min-height: 150px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    text-align: center;
    word-break: break-word;

    span:not(.bag-photo-upload__file-type) {
      font-size: 15px;
      font-weight: 600;
    }

    small {
      font-size: 13px;
      line-height: 1.3;
      color: #707070;
    }
  }

  &__file-type {
    min-width: 58px;
    padding: 7px 10px;
    border-radius: 4px;
    background: #f28c00;
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
  }

  &__slot-overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.88);
    color: #343434;
    font-size: 15px;
    font-weight: 600;
  }

  &__progress {
    display: block;
    width: min(180px, 78%);
    height: 7px;
    overflow: hidden;
    border-radius: 999px;
    background: #d8d8d8;

    span {
      display: block;
      height: 100%;
      border-radius: inherit;
      background: #00a9e0;
      transition: width 0.2s ease;
    }

    &--status {
      width: 100%;
      max-width: 240px;
      margin-left: auto;
      margin-right: auto;
    }
  }

  &__badge {
    position: absolute;
    right: 8px;
    bottom: 8px;
    z-index: 1;
    padding: 4px 8px;
    border-radius: 4px;
    background: #2b9f5b;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
  }

  &__badge--failed {
    background: #b42318;
  }

  &__delete {
    position: absolute;
    right: 8px;
    top: 8px;
    z-index: 3;
    width: 30px;
    height: 30px;
    padding: 6px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.28);
    object-fit: contain;
  }

  &__status {
    display: flex;
    min-height: 38px;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid #cfcfcf;
    border-radius: 6px;
    background: #ffffff;
    font-size: 15px;
    text-align: center;

    &--error {
      border-color: #c44;
      color: #9b1c1c;
    }
  }

  &__status-text {
    width: 100%;
  }

  &__spinner {
    width: 34px;
    height: 34px;
    border: 4px solid #e6e6e6;
    border-top: 4px solid #00a9e0;
    border-radius: 50%;
    animation: bag-photo-upload-spin 1s linear infinite;

    &--small {
      width: 20px;
      height: 20px;
      border-width: 3px;
    }
  }

  &__done {
    margin-top: 0;
  }
}

@keyframes bag-photo-upload-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
